/**
 * Service layer — Room Program tool (docs/ROOM_PROGRAM.md).
 *
 * The Room Program is a fast-entry front end onto the REAL Concept -> Unit -> Level -> Room
 * hierarchy the app already has — there is no separate "preliminary" data model. Every
 * read/write of Units, Levels, and Rooms here is a thin wrapper over the existing functions in
 * services/design.js (never a duplicate implementation), so a room added in this tool is the
 * exact same record Reference > Design > Concepts and the budget chain (Room Area Net ->
 * Budget Room Cost Low/High) already read.
 *
 * The one genuinely new domain is `program_builder_suggestions` (Supabase) — the client-facing
 * propose/review/apply queue, reviewed here and applied server-side by api/program-builder.js.
 */

import {
  getConcepts, createConcept, setSelectedConcept,
  getConceptUnits, createConceptUnit, deleteConceptUnitCascade,
  getConceptLevels, createConceptLevel, updateConceptLevel, deleteConceptLevelCascade,
  getLevelRooms, getRoomTemplates, createRoom, updateRoom, deleteRoom,
} from './design.js'

// Mirrors the legacy Rooms and Spaces "Level" singleSelect sync createRoom already performs
// (design.js's ROOM_LEVEL_SINGLESELECT_OPTIONS is not exported, so this is kept in sync by hand).
const ROOM_LEVEL_SINGLESELECT_OPTIONS = ['Fifth', 'Fourth', 'Third', 'Second', 'First', 'Site']

export { getConcepts, createConcept, setSelectedConcept, createConceptUnit, deleteConceptUnitCascade, createConceptLevel, updateConceptLevel, deleteConceptLevelCascade, createRoom, updateRoom, deleteRoom }

/** The project's currently Selected Concept, or null if none is selected (including "no concepts at all"). */
export async function getSelectedConcept(projectId) {
  const concepts = await getConcepts(projectId)
  return (concepts || []).find(c => c.fields && c.fields['Selected Concept'] === true) || null
}

/**
 * The full Room Program for a concept: Units -> Levels (Stacking Order) -> Rooms, with the room
 * fields this tool edits (Custom Name, Room Template, Area Manual, Scope Type, Room Notes) — a
 * richer shape than design.js's getConceptRoomsGrouped (which only carries the 4 fields AlderCAD
 * needs), built fresh here rather than widening that function's existing return shape.
 */
export async function getRoomProgram(conceptId) {
  const [units, templates] = await Promise.all([getConceptUnits(conceptId), getRoomTemplates()])
  const templateById = new Map(templates.map(t => [t.id, t]))
  const shapeRoom = r => {
    const tplId = Array.isArray(r.fields['Room Template']) ? r.fields['Room Template'][0] : null
    return {
      id: r.id,
      name: r.fields['Custom Name'] || '',
      roomNumber: r.fields['Room Number'] || '',
      scopeType: r.fields['Scope Type'] || '',
      roomTemplateId: tplId,
      roomTemplateName: tplId ? (templateById.get(tplId)?.name || '') : '',
      areaManual: typeof r.fields['Area Manual'] === 'number' ? r.fields['Area Manual'] : null,
      notes: r.fields['Room Notes'] || '',
    }
  }
  const out = []
  for (const u of (units || [])) {
    const levels = await getConceptLevels(u.id)
    const shapedLevels = []
    for (const l of (levels || [])) {
      const rooms = await getLevelRooms(l.id)
      shapedLevels.push({
        id: l.id,
        name: l.fields['Name'] || 'Level',
        stackingOrder: typeof l.fields['Stacking Order'] === 'number' ? l.fields['Stacking Order'] : 0,
        rooms: (rooms || []).map(shapeRoom),
      })
    }
    out.push({
      id: u.id,
      name: u.fields['Unit Type'] || u.fields['Name'] || 'Unit',
      levels: shapedLevels,
    })
  }
  return { units: out, templates }
}

/** Move a room to a different Concept Level — updates the real link plus the legacy `Level`
 *  singleSelect sync createRoom already performs, via the generic updateRoom(). */
export function moveRoomToLevel(roomId, levelId, levelName) {
  const fields = { 'Concept Level': [levelId] }
  const match = ROOM_LEVEL_SINGLESELECT_OPTIONS.find(o => o.toLowerCase() === String(levelName || '').toLowerCase())
  if (match) fields['Level'] = match
  return updateRoom(roomId, fields)
}

/* ── Suggestions (client-proposed changes) ───────────────────────────────────────────────── */

const SUGGESTIONS_API = '/api/program-builder'

async function callApi(action, payload) {
  const token = await (window.__clerkGetToken ? window.__clerkGetToken() : Promise.resolve(null))
  const res = await fetch(SUGGESTIONS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || (action + ' failed'))
  return json
}

/** Pending + recently-reviewed suggestions for a project (Suggestions panel). */
export function listSuggestions(projectId) {
  return callApi('listSuggestions', { projectId }).then(j => j.suggestions || [])
}

/** Approve or reject a suggestion. On approve, the server applies the change to the live Room
 *  (move/edit/add/remove) atomically with marking the suggestion reviewed. */
export function reviewSuggestion(id, decision, note) {
  return callApi('reviewSuggestion', { id, decision, note }).then(j => j.suggestion)
}
